import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { callToFetchStores, callToAutoReplicateListings } from '../../util/api';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const LOAD_STORES_REQUEST = 'app/ProfileSettingsPage/LOAD_STORES_REQUEST';
export const LOAD_STORES_SUCCESS = 'app/ProfileSettingsPage/LOAD_STORES_SUCCESS';
export const LOAD_STORES_ERROR = 'app/ProfileSettingsPage/LOAD_STORES_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  loadingStores: false,
  storesLoaded: false,
  stores: [],
  storesError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case LOAD_STORES_REQUEST:
      return { ...state, loadingStores: true };
    case LOAD_STORES_SUCCESS:
      return {
        ...state,
        loadingStores: false,
        stores: payload,
        storesError: null,
        storesLoaded: true,
      };
    case LOAD_STORES_ERROR:
      return {
        ...state,
        loadingStores: false,
        stores: [],
        storesError: payload,
        storesLoaded: true,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});
export const loadStoresRequest = params => ({
  type: LOAD_STORES_REQUEST,
  payload: { params },
});
export const loadStoresSuccess = result => ({
  type: LOAD_STORES_SUCCESS,
  payload: result,
});
export const loadStoresError = error => ({
  type: LOAD_STORES_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    // Get current user's stores before update (for comparison)
    const state = getState();
    const previousUser = state.user?.currentUser;
    const previousPublicData = previousUser?.attributes?.profile?.publicData || {};
    const previousStores = previousPublicData.stores || [];
    const isManufacturer = previousPublicData.sellerType === 'manufacturer';

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));

        // Auto-replicate listings if manufacturer updated stores
        if (isManufacturer) {
          const newPublicData = actionPayload?.publicData || {};
          const newStores = newPublicData.stores || [];

          // Check if stores array changed
          const storesChanged =
            JSON.stringify(previousStores.sort()) !== JSON.stringify(newStores.sort());

          if (storesChanged && currentUser?.id?.uuid) {
            console.log('[Auto-Replicate] Stores changed, triggering replication...');
            console.log('[Auto-Replicate] Previous stores:', previousStores.length);
            console.log('[Auto-Replicate] New stores:', newStores.length);

            // Trigger auto-replication (non-blocking)
            callToAutoReplicateListings({
              manufacturerUserId: currentUser.id.uuid,
              previousStores,
              newStores,
            })
              .then(result => {
                console.log('[Auto-Replicate] Success:', result);
              })
              .catch(error => {
                console.error('[Auto-Replicate] Error:', error);
                // Don't throw - replication errors shouldn't block profile update
              });
          }
        }
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

export const fetchStores = () => (dispatch, sdk) => {
  dispatch(loadStoresRequest());

  return callToFetchStores()
    .then(res => {
      dispatch(loadStoresSuccess(res.users));
    })
    .catch(e => dispatch(loadStoresError(storableError(e))));
};
